/**
 * Echobot Context
 * Global state management for Echobot across the application
 */

import React, { createContext, useContext, useState, useCallback, useMemo, ReactNode } from 'react';
import {
  EchobotAnimation,
  EchobotContext as EchobotContextType,
  EchobotMessage,
  EchobotState,
  ChatMessage
} from '../types/echobot';

interface EchobotContextValue extends EchobotState {
  // Message control
  showMessage: (content: string, animation?: EchobotAnimation, duration?: number, actionButton?: { label: string; action: string }) => void;
  hideMessage: () => void;
  
  // Animation control
  playAnimation: (animation: EchobotAnimation) => void;
  
  // Context management
  setContext: (context: EchobotContextType) => void;
  
  // Visibility control
  show: () => void;
  hide: () => void;
  toggleMinimized: () => void;
  
  // Chat control
  openChat: () => void;
  closeChat: () => void;
  addChatMessage: (message: ChatMessage) => void;
  clearChatHistory: () => void;
}

const EchobotContext = createContext<EchobotContextValue | null>(null);

export const useEchobot = () => {
  const context = useContext(EchobotContext);
  if (!context) {
    throw new Error('useEchobot must be used within EchobotProvider');
  }
  return context;
};

interface EchobotProviderProps {
  children: ReactNode;
  initialContext?: EchobotContextType;
}

export const EchobotProvider: React.FC<EchobotProviderProps> = ({
  children,
  initialContext = 'dashboard'
}) => {
  const [state, setState] = useState<EchobotState>({
    currentMessage: null,
    isVisible: true,
    isMinimized: false,
    isChatOpen: false,
    currentContext: initialContext,
    chatHistory: []
  });

  // Hide current message (declared before showMessage, which references it)
  const hideMessage = useCallback(() => {
    setState(prev => ({
      ...prev,
      currentMessage: null
    }));
  }, []);

  // Show message with animation
  const showMessage = useCallback((
    content: string,
    animation: EchobotAnimation = 'none',
    duration: number = 4000,
    actionButton?: { label: string; action: string }
  ) => {
    const message: EchobotMessage = {
      id: `msg-${Date.now()}`,
      content,
      animation,
      timestamp: new Date(),
      actionButton
    };

    setState(prev => ({
      ...prev,
      currentMessage: message
    }));

    // Auto-hide after duration
    if (duration > 0) {
      setTimeout(() => {
        hideMessage();
      }, duration);
    }
  }, [hideMessage]);

  // Play animation
  const playAnimation = useCallback((animation: EchobotAnimation) => {
    setState(prev => {
      const currentMessage = prev.currentMessage;
      if (currentMessage) {
        return {
          ...prev,
          currentMessage: {
            ...currentMessage,
            animation
          }
        };
      }
      return prev;
    });
  }, []);

  // Set current context. Bail out when unchanged so React skips the re-render
  // (returning `prev` is an Object.is no-op) — this prevents an infinite render
  // loop when consumers call setContext from an effect.
  const setContext = useCallback((context: EchobotContextType) => {
    setState(prev => (prev.currentContext === context ? prev : {
      ...prev,
      currentContext: context
    }));
  }, []);

  // Show Echobot
  const show = useCallback(() => {
    setState(prev => ({
      ...prev,
      isVisible: true
    }));
  }, []);

  // Hide Echobot
  const hide = useCallback(() => {
    setState(prev => ({
      ...prev,
      isVisible: false
    }));
  }, []);

  // Toggle minimized state
  const toggleMinimized = useCallback(() => {
    setState(prev => ({
      ...prev,
      isMinimized: !prev.isMinimized
    }));
  }, []);

  // Open chat
  const openChat = useCallback(() => {
    setState(prev => ({
      ...prev,
      isChatOpen: true
    }));
  }, []);

  // Close chat
  const closeChat = useCallback(() => {
    setState(prev => ({
      ...prev,
      isChatOpen: false
    }));
  }, []);

  // Add chat message
  const addChatMessage = useCallback((message: ChatMessage) => {
    setState(prev => ({
      ...prev,
      chatHistory: [...prev.chatHistory, message]
    }));
  }, []);

  // Clear chat history
  const clearChatHistory = useCallback(() => {
    setState(prev => ({
      ...prev,
      chatHistory: []
    }));
  }, []);

  // Memoize so the context value reference is stable across renders (only
  // changes when `state` does). Without this, every provider render hands
  // consumers a new object, re-triggering their context-dependent effects.
  const value = useMemo<EchobotContextValue>(() => ({
    ...state,
    showMessage,
    hideMessage,
    playAnimation,
    setContext,
    show,
    hide,
    toggleMinimized,
    openChat,
    closeChat,
    addChatMessage,
    clearChatHistory
  }), [state, showMessage, hideMessage, playAnimation, setContext, show, hide, toggleMinimized, openChat, closeChat, addChatMessage, clearChatHistory]);

  return (
    <EchobotContext.Provider value={value}>
      {children}
    </EchobotContext.Provider>
  );
};

// Export context for testing
export { EchobotContext };

